import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface Chat {
  id: string;
  is_group: boolean;
  group_name: string | null;
  group_image_url: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  participants?: ChatParticipant[];
  last_message?: Message | null;
}

export interface ChatParticipant {
  id: string;
  chat_id: string;
  user_id: string;
  joined_at: string;
  is_admin: boolean;
  profile?: { first_name: string | null; last_name: string | null; avatar_url: string | null; email: string | null };
}

export interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string | null;
  message_type: string;
  media_url: string | null;
  is_delivered: boolean;
  is_read: boolean;
  created_at: string;
}

export interface Contact {
  id: string;
  user_id: string;
  contact_user_id: string;
  nickname: string | null;
  created_at: string;
  profile?: { first_name: string | null; last_name: string | null; avatar_url: string | null; email: string | null };
}

export const useMessaging = () => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      console.log("[Messaging] Session:", session?.user?.id, "token:", !!session?.access_token);
      setCurrentUserId(session?.user?.id || null);
    };
    getUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUserId(session?.user?.id || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchChats = useCallback(async () => {
    if (!currentUserId) return;
    setLoading(true);
    try {
      // Get chats user participates in
      const { data: participantData } = await supabase
        .from("chat_participants")
        .select("chat_id")
        .eq("user_id", currentUserId);

      if (!participantData?.length) {
        setChats([]);
        setLoading(false);
        return;
      }

      const chatIds = participantData.map(p => p.chat_id);
      const { data: chatsData } = await supabase
        .from("chats")
        .select("*")
        .in("id", chatIds)
        .order("updated_at", { ascending: false });

      if (!chatsData) {
        setChats([]);
        setLoading(false);
        return;
      }

      // Fetch participants and last message for each chat
      const enrichedChats = await Promise.all(
        chatsData.map(async (chat) => {
          const { data: participants } = await supabase
            .from("chat_participants")
            .select("*")
            .eq("chat_id", chat.id);

          // Get profiles for participants
          const enrichedParticipants = await Promise.all(
            (participants || []).map(async (p) => {
              const { data: profile } = await supabase
                .from("profiles")
                .select("first_name, last_name, avatar_url, email")
                .eq("user_id", p.user_id)
                .single();
              return { ...p, profile };
            })
          );

          const { data: lastMsg } = await supabase
            .from("messages")
            .select("*")
            .eq("chat_id", chat.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();

          return { ...chat, participants: enrichedParticipants, last_message: lastMsg };
        })
      );

      setChats(enrichedChats);
    } catch (err) {
      console.error("Error fetching chats:", err);
    }
    setLoading(false);
  }, [currentUserId]);

  const fetchContacts = useCallback(async () => {
    if (!currentUserId) return;
    const { data } = await supabase
      .from("contacts")
      .select("*")
      .eq("user_id", currentUserId);

    if (!data) return;

    const enriched = await Promise.all(
      data.map(async (c) => {
        const { data: profile } = await supabase
          .from("profiles")
          .select("first_name, last_name, avatar_url, email")
          .eq("user_id", c.contact_user_id)
          .single();
        return { ...c, profile };
      })
    );
    setContacts(enriched);
  }, [currentUserId]);

  useEffect(() => {
    if (currentUserId) {
      fetchChats();
      fetchContacts();
    }
  }, [currentUserId, fetchChats, fetchContacts]);

  // Realtime subscription
  useEffect(() => {
    if (!currentUserId) return;

    const channel = supabase
      .channel("messaging-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, () => {
        fetchChats();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentUserId, fetchChats]);

  const addContactByEmail = async (email: string) => {
    if (!currentUserId) return null;

    // Use security definer function to find user by email (avoids RLS restrictions)
    const { data: userId, error: findError } = await supabase
      .rpc("find_user_by_email", { _email: email });

    const profile = userId ? { user_id: userId } : null;

    if (!profile) {
      toast({ title: "Utente non trovato", description: "Nessun utente registrato con questa e-mail.", variant: "destructive" });
      return null;
    }

    if (profile.user_id === currentUserId) {
      toast({ title: "Errore", description: "Non puoi aggiungere te stesso.", variant: "destructive" });
      return null;
    }

    const { error } = await supabase.from("contacts").insert({ user_id: currentUserId, contact_user_id: profile.user_id });

    if (error) {
      if (error.code === "23505") {
        toast({ title: "Contatto esistente", description: "Questo contatto è già nella tua lista." });
      } else {
        toast({ title: "Errore", description: error.message, variant: "destructive" });
      }
      return null;
    }

    toast({ title: "Contatto aggiunto!" });
    fetchContacts();
    return profile.user_id;
  };

  const createChat = async (participantIds: string[], isGroup = false, groupName?: string) => {
    if (!currentUserId) return null;

    // For 1-to-1, check if chat already exists
    if (!isGroup && participantIds.length === 1) {
      const existing = chats.find(c =>
        !c.is_group &&
        c.participants?.length === 2 &&
        c.participants.some(p => p.user_id === participantIds[0])
      );
      if (existing) return existing.id;
    }

    console.log("[Messaging] Creating chat, currentUserId:", currentUserId, "isGroup:", isGroup);
    
    // Use RPC function to create chat + participants atomically (avoids RLS issue)
    const { data: chatId, error } = await supabase.rpc("create_chat_with_participants", {
      _is_group: isGroup,
      _group_name: groupName || null,
      _participant_ids: participantIds,
    });

    console.log("[Messaging] Chat creation result:", { chatId, error });

    if (error || !chatId) {
      toast({ title: "Errore", description: error?.message || "Impossibile creare la chat.", variant: "destructive" });
      return null;
    }

    await fetchChats();
    return chatId;
  };

  const sendMessage = async (chatId: string, content: string, type = "text", mediaUrl?: string) => {
    if (!currentUserId) return;

    await supabase.from("messages").insert({
      chat_id: chatId,
      sender_id: currentUserId,
      content,
      message_type: type,
      media_url: mediaUrl || null,
    });

    // Update chat timestamp
    await supabase.from("chats").update({ updated_at: new Date().toISOString() }).eq("id", chatId);

    // Send push notification to other participants
    try {
      const { data: participants } = await supabase
        .from("chat_participants")
        .select("user_id")
        .eq("chat_id", chatId)
        .neq("user_id", currentUserId);

      if (participants && participants.length > 0) {
        // Get sender name
        const { data: senderProfile } = await supabase
          .from("profiles")
          .select("first_name, last_name, email")
          .eq("user_id", currentUserId)
          .maybeSingle();

        const senderName = senderProfile
          ? [senderProfile.first_name, senderProfile.last_name].filter(Boolean).join(" ") || senderProfile.email || "Utente"
          : "Utente";

        const messagePreview = type === "text"
          ? (content.length > 60 ? content.substring(0, 60) + "…" : content)
          : type === "voice" ? "🎤 Messaggio vocale"
          : type === "video" ? "🎬 Video"
          : "📎 Media";

        const { sendPushToUser } = await import("@/lib/pushNotifications");
        for (const p of participants) {
          sendPushToUser(
            p.user_id,
            senderName,
            messagePreview,
            `/messaggi?chat=${chatId}`,
            `msg-${chatId}`
          ).catch(() => {});
        }
      }
    } catch (e) {
      console.log("[Push] Message notification failed:", e);
    }
  };

  const fetchMessages = async (chatId: string) => {
    const { data } = await supabase
      .from("messages")
      .select("*")
      .eq("chat_id", chatId)
      .order("created_at", { ascending: true });
    return data || [];
  };

  const markAsRead = async (chatId: string) => {
    if (!currentUserId) return;
    await supabase
      .from("messages")
      .update({ is_read: true })
      .eq("chat_id", chatId)
      .neq("sender_id", currentUserId)
      .eq("is_read", false);
  };

  const uploadMedia = async (file: File, chatId: string) => {
    const path = `${currentUserId}/${chatId}/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("chat-media").upload(path, file);
    if (error) {
      toast({ title: "Errore upload", description: error.message, variant: "destructive" });
      return null;
    }
    const { data: urlData } = supabase.storage.from("chat-media").getPublicUrl(path);
    return urlData.publicUrl;
  };

  const getChatDisplayName = (chat: Chat) => {
    if (chat.is_group) return chat.group_name || "Gruppo";
    const other = chat.participants?.find(p => p.user_id !== currentUserId);
    if (!other?.profile) return "Utente";
    const { first_name, last_name, email } = other.profile;
    if (first_name || last_name) return `${first_name || ""} ${last_name || ""}`.trim();
    return email || "Utente";
  };

  const getChatAvatar = (chat: Chat) => {
    if (chat.is_group) return chat.group_image_url;
    const other = chat.participants?.find(p => p.user_id !== currentUserId);
    return other?.profile?.avatar_url || null;
  };

  return {
    chats, contacts, currentUserId, loading,
    addContactByEmail, createChat, sendMessage, fetchMessages,
    markAsRead, uploadMedia, getChatDisplayName, getChatAvatar,
    fetchChats, fetchContacts,
  };
};
